// i18n
import { translateInComponent } from 'infras/i18n';
import translations from './i18n.json';

import { ReactNode, RefObject } from 'react';
import styled from '@emotion/styled';

import EventTypeHostMessage from 'presentations/views/components/molecules/EventTypeHostMessage';
import Icon from 'presentations/views/components/atoms/Icon';
import IconButton from 'presentations/views/components/atoms/IconButton';
import ConnectJicooHeader from 'presentations/views/components/molecules/ConnectJicooHeader';
import {
  spacing,
  fontSize,
  mediaMax,
  fontWeight,
  isMediaMax,
} from 'presentations/views/styles';
import { gray04, white } from 'presentations/views/styles/color';
import Headline from 'presentations/views/components/atoms/Headline';
import {
  calendarIntegrationsPath,
  eventTypePath,
  guestEventReschedulingsPath,
} from 'presentations/routes/path';
import { CalendarViewType } from 'domains/models/event_type/calendarViewType';
import { ViewerItem } from 'presentations/api/schema/user';
import { CalendarDateDetail } from 'presentations/api/helpers/event_type';

export enum Steps {
  Date = 1,
  DateWeekly = 10,
  Time = 2,
  Location = 3,
  Service = 4,
  Contact = 5,
  PaymentMethod = 6,
  PaymentPreview = 7,
  Complete = 8,
}

export enum AccountConnectMode {
  connected,
  notConnected,
  connect,
}

export interface Props {
  viewer: ViewerItem | null;
  calendarDateList: CalendarDateDetail[];
  title: string;
  teamSlug: string;
  isWidget?: boolean;
  description?: string;
  onClickBack?: any;
  isShowBack: boolean;
  isOpen: boolean;
  isInitLoaded?: boolean;
  form: ReactNode;
  containerRef?: RefObject<HTMLDivElement>;
  accountConnectMode: AccountConnectMode | null;
  eventTypeSlug: string;
  color: string;
  onClickCalendarSetting?: any;
  editingEventUid: string | undefined;
  activeStep: Steps;
  calendarViewType: CalendarViewType;
  hiddenConnectRecommend?: boolean;
  hasTeamHeaderLogo?: boolean;
  isOwnEventType?: boolean;
  isHostDiagnosticMode?: boolean;
  onClickHostDianosticMode?: Function;
  isPollPage?: boolean;
  hostImage: string | null;
  onChangeViewType?: (viewType: CalendarViewType) => void;
  isShowViewerCalendar: boolean;
  onChangeIsShowViewerCalendar: (isShowViewerCalendar: boolean) => void;
}

export default function Component(props: Props) {
  const t = translateInComponent(translations);

  const onChangeViewType = (args: { value: number | string }) => {
    if (!props.onChangeViewType) return;
    props.onChangeViewType(Number(args.value) as CalendarViewType);
  };

  const isCalendarConnected =
    props.accountConnectMode === AccountConnectMode.connected;
  const isShowConnectCalendar =
    !props.isWidget &&
    !props.hiddenConnectRecommend &&
    props.editingEventUid === undefined &&
    isCalendarConnected;

  const isTabletTimeForm =
    isMediaMax('tablet') &&
    props.activeStep === Steps.Time &&
    [
      CalendarViewType.Month,
      CalendarViewType.TwoColumnMonth,
      CalendarViewType.TwoColumnMonthAccent,
    ].includes(props.calendarViewType);

  const content = (
    <ActionView ref={props.containerRef}>
      <StyledHeaderSection
        calendarViewType={props.calendarViewType}
        isPollPage={props.isPollPage}
        isTabletTimeForm={isTabletTimeForm}
        isWidget={props.isWidget}
        isShowConnectCalendar={isShowConnectCalendar}
      >
        <StyledHeader
          isTabletTimeForm={isTabletTimeForm}
          isWidget={props.isWidget}
        >
          {props.isShowBack && (
            <LeftItem isWidget={props.isWidget}>
              <BackIconButton onClick={props.onClickBack}>
                <BackIcon
                  iconColor={props.color}
                  color="inherit"
                  name="chevron-left"
                  size={1.3}
                />
              </BackIconButton>
            </LeftItem>
          )}
          <ActionHeader>
            <HeadlineContainer>
              <ActionHeadline variant="h2">
                <div>{props.title}</div>
                {/* <StyledToggleButtonGroup
                  value={props.calendarViewType}
                  onChange={onChangeViewType}
                  items={[
                    {
                      tooltip: t('予約カレンダーをカレンダー形式で表示します'),
                      label: (
                        <ViewTypeIcon
                          name="calendar"
                          isActive={[
                            CalendarViewType.WeekCalendar,
                            CalendarViewType.CalendarWeekLegacy,
                          ].includes(props.calendarViewType)}
                        />
                      ),
                      value: CalendarViewType.WeekCalendar,
                    },
                    {
                      tooltip: t('予約カレンダーを週ビュー形式で表示します'),
                      label: (
                        <ViewTypeIcon
                          name="view-week-outline"
                          isActive={
                            props.calendarViewType === CalendarViewType.Week
                          }
                        />
                      ),
                      value: CalendarViewType.Week,
                    },
                    {
                      tooltip: t(
                        '予約カレンダーを月＋週ビュー形式で表示します',
                      ),
                      label: (
                        <ViewTypeIcon
                          name="view-day-outline"
                          isActive={
                            props.calendarViewType ===
                            CalendarViewType.TwoColumnMonth
                          }
                        />
                      ),
                      value: CalendarViewType.TwoColumnMonth,
                    },
                  ]}
                /> */}
              </ActionHeadline>
            </HeadlineContainer>
            {props.description && (
              <EventTypeHostMessage
                description={props.description}
                isAnimate={props.isOpen}
              />
            )}
          </ActionHeader>
        </StyledHeader>
      </StyledHeaderSection>
      {!props.isWidget && (
        <StyledConnectStatusContainer
          calendarViewType={props.calendarViewType}
          isTabletTimeForm={isTabletTimeForm}
        >
          <ConnectJicooHeader
            linkTo={eventTypePath({
              teamSlug: props.teamSlug,
              eventTypeSlug: props.eventTypeSlug,
            })}
            isConnected={isCalendarConnected}
            color={props.color}
            isShowViewerCalendar={props.isShowViewerCalendar}
            onChangeIsShowViewerCalendar={props.onChangeIsShowViewerCalendar}
            onClickSetting={props.onClickCalendarSetting}
            settingLink={calendarIntegrationsPath({
              from: encodeURIComponent(
                props.editingEventUid === undefined
                  ? eventTypePath({
                      teamSlug: props.teamSlug,
                      eventTypeSlug: props.eventTypeSlug,
                    })
                  : guestEventReschedulingsPath(props.editingEventUid),
              ),
            })}
          />
        </StyledConnectStatusContainer>
      )}
      <StyledNoJicooBrandSizedBox />
      {props.isOpen && (
        <StyledFormContainer
          calendarViewType={props.calendarViewType}
          isTabletTimeForm={isTabletTimeForm}
        >
          {props.form}
        </StyledFormContainer>
      )}
    </ActionView>
  );

  return <Container>{content}</Container>;
}

const Container = styled.div`
  min-width: 320px;
  width: 100%;
  ${mediaMax.tablet} {
    min-width: 0;
  }
`;
const ActionView = styled.div`
  flex-shrink: 0;
  width: 385px;
  height: auto;
  background-color: ${white};
  width: 100%;
  margin-left: auto;
  margin-right: auto;
`;
const HeadlineContainer = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
`;
const ActionHeadline = styled(Headline)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: ${fontSize(20)};
  width: 100%;
  ${mediaMax.tablet} {
    flex-direction: column;
    font-size: ${fontSize(18)};
  }
`;
const StyledHeaderSection = styled.div<{
  calendarViewType: CalendarViewType;
  isWidget?: boolean;
  isPollPage?: boolean;
  isTabletTimeForm: boolean;
  isShowConnectCalendar: boolean;
}>`
  position: relative;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  padding-left: ${(props) => (props.isWidget ? 0 : spacing(24))};
  padding-right: ${(props) => (props.isWidget ? 0 : spacing(24))};
  padding-top: ${spacing(20)};
  padding-bottom: ${spacing(20)};
  border-bottom: 1px solid ${gray04};
  ${mediaMax.desktop} {
    border-top: 1px solid ${gray04};
    margin-top: ${spacing(16)};
  }
  ${mediaMax.tablet} {
    border-top: none;
    margin-top: 0;
    flex-direction: column;
    align-items: center;
  }

  ${(props) => {
    if (props.isPollPage) {
      return `
        margin-top: 0 !important;
        border-top: none !important;
      `;
    } else {
      if (props.isTabletTimeForm) {
        return `
          padding: 0 !important;
          border-bottom: none;
        `;
      }
      if (props.calendarViewType === CalendarViewType.Month) {
        return `
          padding: ${spacing(40)} 0 ${spacing(24)};
          flex-direction: column;
          border-top: none;
          ${mediaMax.desktop} {
            border-top: none;
            margin-top: 0;
          }
          ${mediaMax.tablet} {
            border-top: none;
          }
          ${mediaMax.smartphone} {
            padding: ${spacing(24)} 0;
          }
        `;
      }
    }
  }}
`;
const StyledHeader = styled.div<{
  isTabletTimeForm: boolean;
  isWidget?: boolean;
}>`
  display: flex;
  flex: 1;
  align-items: center;
  width: 100%;

  ${(props) => {
    if (!props.isWidget && props.isTabletTimeForm) {
      return `
        display: none;
      `;
    }
  }};
`;
const ActionHeader = styled.div`
  width: 100%;
  ${mediaMax.tablet} {
    padding: 0 ${spacing(8)};
  }
`;
const LeftItem = styled.div<{ isWidget?: boolean }>`
  left: ${spacing(16)};
  ${mediaMax.tablet} {
    position: absolute;
    ${(props) =>
      props.isWidget &&
      `
      position: relative;
      left: 0;
    `}
  }

  & > button {
    padding: 0;
    margin-right: ${spacing(8)};
  }
`;

const BackIcon = styled(Icon)<{ iconColor: string }>`
  fill: ${({ iconColor }) => iconColor};
  font-size: ${fontSize(16)};
  font-weight: ${fontWeight('bold')};
`;
const BackIconButton = styled(IconButton)``;

const StyledFormContainer = styled.div<{
  calendarViewType: CalendarViewType;
  isTabletTimeForm: boolean;
}>`
  ${(props) => {
    if (props.isTabletTimeForm) {
      return `
        padding: 0 !important;
      `;
    } else if (props.calendarViewType === CalendarViewType.Month) {
      return `
        padding: 0 !important;
      `;
    } else if (
      [
        CalendarViewType.TwoColumnMonth,
        CalendarViewType.TwoColumnMonthAccent,
      ].includes(props.calendarViewType)
    ) {
      return `
        padding: 0 ${spacing(24)} ${spacing(16)};
        ${mediaMax.tablet} {
          padding: 0 ${spacing(12)} ${spacing(16)};
        }
      `;
    } else {
      return `
        padding: 0 ${spacing(24)} ${spacing(16)};
        ${mediaMax.tablet} {
          padding: 0 ${spacing(12)} ${spacing(16)};
        }
      `;
    }
  }}
`;
const StyledNoJicooBrandSizedBox = styled.div`
  margin-top: ${spacing(16)};
`;

const StyledConnectStatusContainer = styled.div<{
  calendarViewType: CalendarViewType;
  isTabletTimeForm: boolean;
}>`
  display: flex;
  justify-content: space-between;
  ${(props) => {
    if (props.isTabletTimeForm) {
      return `
        padding: ${spacing(24)} 0;
      `;
    }
    if (props.calendarViewType === CalendarViewType.Month) {
      return `
        padding: ${spacing(16)} 0 ${spacing(8)};
      `;
    } else {
      return `
        padding:${spacing(16)} ${spacing(24)} ${spacing(8)};
        ${mediaMax.tablet} {
          padding:${spacing(16)} ${spacing(16)} ${spacing(8)};
        }
      `;
    }
  }}
  ${mediaMax.tablet} {
    justify-content: center;
  }
`;
